import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { FontSizes, TemplateState } from '../../../types/common/types';
import { PhasrRenderIconSet } from '../../../phasrRecommendations/uiUtils/iconSet';
import { PhasrIcons } from '../../../phasrRecommendations/uiUtils/types';
import { ButtonColor, ButtonSize } from '../../../ui/gzIconButton/types';
import { render } from 'lit';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { translateFn } from "../../../utils/translator.helper";
import { GzButtonWebComponent } from "../../../ui/gzButton";
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { LinkColor, LinkType } from '../../../ui/gzLinkComponent/types';
import { BEHAVIORAL_PROFILE_DISPLAY_LIMIT, IncidentData } from '../types';
import { PhasrMonitoredRulesDetailsPanelComponent } from '../component/PhasrMonitoredRulesDetailsPanelComponent';

export class PhasrMonitoredRulesDetailsPanelView extends BaseWebComponentView {
  public moreIncidents = false;

  public dom: {
    $wrapper: HTMLElement;
    $loadingSpinner: HTMLElement;
    $sectionTitleLabel: HTMLElement;
    $targetedActivityType: HTMLElement;
    $targetedActivityTypeLabel: HTMLElement;
    $ruleTriggerDate: HTMLElement;
    $ruleTriggerDateLabel: HTMLElement;
    $ruleTrigger: HTMLElement;
    $ruleTriggersLabel: HTMLElement;
    $recommendations: HTMLElement;
    $recommendationsLabel: HTMLElement;
    $behavioralProfilesWrapper: GzTooltipWebComponent;
    $behavioralProfilesLabel: HTMLElement;
    $restrictedBehavioralProfilesWrapper: GzTooltipWebComponent;
    $restrictedBehavioralProfilesLabel: HTMLElement;
    $incidentsInvolvingRuleTriggers: HTMLElement;
    $incidentsInvolvingRuleTriggersLabel: HTMLElement;
    $incidentsInvolvingRuleTriggersShowMore: HTMLElement;
    $monitoredRuleDescription: HTMLElement;
    $monitoredRuleDescriptionLabel: HTMLElement;
    $sectionRowFirstRule: HTMLElement;
    $sectionRowSecondRule: HTMLElement;
    $sectionRowRules: HTMLElement;
    $rulesTitle: GzTitleWebComponent;
    $accessButton: GzButtonWebComponent;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper : this.shadow.querySelector('.wrapper'),
      $loadingSpinner: this.shadow.querySelector('.spinner'),
      $sectionTitleLabel: this.shadow.querySelector('#sectionTitleLabel'),
      $targetedActivityType: this.shadow.querySelector('#targetedActivityType'),
      $targetedActivityTypeLabel: this.shadow.querySelector('#targetedActivityTypeLabel'),
      $ruleTriggerDate: this.shadow.querySelector('#ruleTriggerDate'),
      $ruleTriggerDateLabel: this.shadow.querySelector('#ruleTriggerDateLabel'),
      $ruleTrigger: this.shadow.querySelector('#ruleTriggers'),
      $ruleTriggersLabel: this.shadow.querySelector('#ruleTriggersLabel'),
      $recommendations: this.shadow.querySelector('#recommendations'),
      $recommendationsLabel: this.shadow.querySelector('#recommendationsLabel'),
      $behavioralProfilesWrapper: this.shadow.querySelector('#behavioralProfilesWrapper'),
      $behavioralProfilesLabel: this.shadow.querySelector('#behavioralProfilesLabel'),
      $restrictedBehavioralProfilesWrapper: this.shadow.querySelector('#restrictedBehavioralProfilesWrapper'),
      $restrictedBehavioralProfilesLabel: this.shadow.querySelector('#restrictedBehavioralProfilesLabel'),
      $incidentsInvolvingRuleTriggers: this.shadow.querySelector('#incidentsInvolvingRuleTriggers'),
      $incidentsInvolvingRuleTriggersLabel: this.shadow.querySelector('#incidentsInvolvingRuleTriggersLabel'),
      $incidentsInvolvingRuleTriggersShowMore: this.shadow.querySelector('#incidentsInvolvingRuleTriggersShowMore'),
      $monitoredRuleDescription: this.shadow.querySelector('#monitoredRuleDescription'),
      $monitoredRuleDescriptionLabel: this.shadow.querySelector('#monitoredRuleDescriptionLabel'),
      $sectionRowFirstRule: this.shadow.querySelector('.first-rule'),
      $sectionRowSecondRule: this.shadow.querySelector('.second-rule'),
      $sectionRowRules: this.shadow.querySelectorAll('.section-row-rule'),
      $rulesTitle: this.shadow.querySelector('#rulesTitle'),
      $accessButton: this.shadow.querySelector('#accessButton'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings(): void {

    this.dom.$sectionTitleLabel.innerText = translateFn('General');
    this.dom.$targetedActivityTypeLabel.innerText = translateFn('Targeted activity type');
    this.dom.$ruleTriggerDateLabel.innerText = translateFn('Rule trigger date');
    this.dom.$ruleTriggersLabel.innerText = translateFn('Rule triggers');
    this.dom.$recommendationsLabel.innerText = translateFn('Recommendations');
    this.dom.$behavioralProfilesLabel.innerText = translateFn('Behavioral profiles');
    this.dom.$restrictedBehavioralProfilesLabel.innerText = translateFn('Restricted behavioral profiles');
    this.dom.$incidentsInvolvingRuleTriggersLabel.innerText = translateFn('Incidents involving rule triggers');
    this.dom.$monitoredRuleDescriptionLabel.innerText = translateFn('Monitored rule description');
    this.dom.$accessButton.innerText = translateFn('Edit access');
  }

  buildSuccessView(data: PhasrMonitoredRulesDetailsPanelComponent): void {
    this.dom.$rulesTitle.innerHTML = data.ruleName;
    this.dom.$targetedActivityType.innerHTML = data.generalDetails.targetActivityType;
    this.dom.$ruleTriggerDate.innerHTML = data.generalDetails.ruleTriggerDate;
    this.dom.$ruleTrigger.innerHTML = data.generalDetails.ruleTriggers === 0 ? '-' : String(data.generalDetails.ruleTriggers);
    this.dom.$recommendations.innerHTML = data.generalDetails.recommendations === 0 ? '-' : String(data.generalDetails.recommendations);
    this.buildBehavioralProfilesDisplayedData(data.generalDetails.behavioralProfiles, this.dom.$behavioralProfilesWrapper);
    this.buildBehavioralProfilesDisplayedData(data.generalDetails.restrictedBehavioralProfiles, this.dom.$restrictedBehavioralProfilesWrapper);
    this.buildIncidentsInvolvingRuleTriggers(data.generalDetails.incidentsInvolvingRuleTriggers ?? [], true);
    this.dom.$monitoredRuleDescription.innerHTML = data.monitoredRuleDescription;
    this.dom.$accessButton.disabled = !data.generalDetails.phasrUsable || (data.generalDetails.behavioralProfiles === 0 && data.generalDetails.restrictedBehavioralProfiles === 0);
  }

  private buildBehavioralProfilesDisplayedData(data: number, tooltipWrapper: GzTooltipWebComponent) {
    const tempIconContainer = document.createElement('div');
    render(
      PhasrRenderIconSet.createIcon(
        PhasrIcons.BEHAVIORAL_PROFILES_ICON,
        ButtonColor.SECONDARY,
        ButtonSize.SIZE_24,
        'svg-icon'
      ),
      tempIconContainer
    );
    if (data === 0) {
      tooltipWrapper.innerHTML = `<gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">-</gz-text>`;
      tooltipWrapper.style.pointerEvents = 'none';
    } else {
      tooltipWrapper.tooltipText = data > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? data.toString() : '';
      tooltipWrapper.innerHTML = `
              ${tempIconContainer.innerHTML}
              <gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">
                ${data > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? BEHAVIORAL_PROFILE_DISPLAY_LIMIT + '+' : data}
              </gz-text>
            `;
    }
  }

  public buildIncidentsInvolvingRuleTriggers(data: IncidentData[], initialDataSetup: boolean = false) {
    if (initialDataSetup && data.length === 0) {
      this.dom.$incidentsInvolvingRuleTriggers.innerHTML = '-';
      return;
    }
    data.forEach(incidentData => {
      const link = new GzLinkWebComponent();
      link.type = LinkType.QUIET;
      link.color = LinkColor.PRIMARY;
      link.style.marginBottom = 'var(--spacer)';
      link.innerText = '#' + incidentData.incidentNumber.toString();
      link.addEventListener("click", () => this.handleIncidentNumberClick(incidentData.incidentId));
      this.dom.$incidentsInvolvingRuleTriggers.appendChild(link);
    });

    if (this.moreIncidents) {
      toggleVisibility(this.dom.$incidentsInvolvingRuleTriggersShowMore, true);
      this.dom.$incidentsInvolvingRuleTriggersShowMore.innerHTML = translateFn('Show more');
    } else {
      toggleVisibility(this.dom.$incidentsInvolvingRuleTriggersShowMore, false);
    }
  }
  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private handleIncidentNumberClick(incident: string) {
    Application.lib.Navigation.openAppPageInNewTab('/incidents/' + incident);
  }
}
